import { memo, useContext } from 'react';
import {
    ArrowRightIcon,
    Button,
    HStack,
    Text,
    VStack,
} from '@hst-horizon/horizon-ui';
import { FormattedMessage } from 'react-intl';
import { clsx } from 'clsx';
import type { SearchResultProduct } from '../../../../../product/queries/useProductSearch';
import { AppContext } from '../../../../../core/context';
import UnhandledError from '../../../../../core/components/UnhandledError';
import { ProgressSpinner } from '../../../../../core/components/ProgressIndicators';
import styles from './SearchField.module.scss';
import { ProductListItem } from './SearchResults';

const maxPreviewItems = 4;

const ProductsResult = memo(
    ({
        products,
        isLoading,
        error,
        search,
        onClick,
        submitSearch,
    }: {
        isLoading: boolean;
        products: SearchResultProduct[];
        error?: Error;
        search: string;
        onClick?: () => void;
        submitSearch(): void;
    }) => {
        const { isInMobileView } = useContext(AppContext);

        if (error) {
            return <UnhandledError>{error}</UnhandledError>;
        }
        if (isLoading) {
            return (
                <div className={styles.loading}>
                    <ProgressSpinner data-test-class="searchPreviewResultsLoading" />
                </div>
            );
        }

        if (search.length < 3) {
            return (
                <Text secondary className={styles.item}>
                    <FormattedMessage id="Please continue typing to see relevant search results ..." />
                </Text>
            );
        }

        const firstProducts = products.slice(0, maxPreviewItems);
        const isEmpty = firstProducts.length === 0;

        if (isEmpty) {
            return (
                <Text secondary regular className={styles.item}>
                    <FormattedMessage id="sorry, no products matching your search could be found." />
                </Text>
            );
        }

        return (
            <VStack expanded>
                <Text
                    className={clsx(styles.item, {
                        [styles.borderBottom]: !isInMobileView,
                    })}
                >
                    <FormattedMessage id="Header Search Results For" />
                    &nbsp;
                    <Text bold>&quot;{search}&quot;</Text>
                </Text>
                {firstProducts.map(product => (
                    <ProductListItem
                        key={product.id}
                        product={product}
                        query={search}
                        onClick={onClick}
                    />
                ))}
                {!isInMobileView ? (
                    <Button text inline onClick={submitSearch}>
                        <HStack
                            gap="mini"
                            align="center"
                            data-test-class="searchPreviewSubmitSearch"
                            className={clsx(styles.item, styles.searchAll)}
                        >
                            <Text secondary semiBold>
                                <FormattedMessage id="Header Search Show All Results" />
                            </Text>
                            <ArrowRightIcon />
                        </HStack>
                    </Button>
                ) : (
                    <Button fullWidth justify="center" onClick={submitSearch}>
                        <HStack
                            gap="mini"
                            align="center"
                            className={clsx(styles.item, styles.searchAll)}
                            data-test-class="searchPreviewSubmitSearch"
                        >
                            <Text secondary semiBold>
                                <FormattedMessage id="Header Search Show All Results" />
                            </Text>
                            <ArrowRightIcon />
                        </HStack>
                    </Button>
                )}
            </VStack>
        );
    }
);

export default ProductsResult;
